<template>
  <nav :class="{ finalize: $route.name !== 'exams-id', completed: exam.completed, 'review-questions': $store.state.exams.reviewQuestions }">
    <div v-if="exam.completed" class="back" @click="$router.go(-1)">
      <Arrow />
    </div>
    <div class="name">
      {{ exam.typeDisplay }}: <b>{{ exam.name }}</b>
    </div>
    <div :class="{ timewarning, completed: exam.completed }" class="timer">
      <span class="clock"><TimeIcon /></span> {{ remaining && !exam.completed ? `${remaining.hours}:${remaining.minutes}:${remaining.seconds}` : '00:00:00' }}
    </div>
    <div v-show="$route.name === 'exams-id' || $route.name === 'resources-center-exams-id'" class="review">
      <div class="item-wrapper" @click="$store.dispatch('exams/reviewQuestions')">
        <Arrow />
        Review Questions
      </div>
    </div>
    <ReviewQuestions v-show="$route.name === 'exams-id' || $route.name === 'resources-center-exams-id'" :class="{ expand: $store.state.exams.reviewQuestions }" />
  </nav>
</template>
<script>
import { mapState } from 'vuex';
import { countDownToDate } from '../utils/helper';
import TimeIcon from '../assets/svg/icon-exam-clock.svg?vue-component';
import Arrow from '../assets/svg/icon-arrow-left.svg?vue-component';
import ReviewQuestions from './ReviewQuestions';

export default {
  components: { Arrow, TimeIcon, ReviewQuestions },
  data: () => ({
    timewarning: false,
  }),
  computed: {
    ...mapState({
      exam: (state) => state.exams.exam,
      now: (state) => state.exams.now,
      timer: (state) => state.exams.timer,
    }),
    remaining() {
      if (!process.client || !this.timer || !this.now) {
        return false;
      }

      const diff = (+this.exam.endTimestampClient || +this.exam.endTimestamp) - this.now;

      const remaining = countDownToDate(this.now, this.end, diff);

      if (!remaining) {
        this.$emit('finished', remaining);
        return false;
      }

      if (+remaining.hours === 0 && +remaining.minutes <= 5 && (this.exam.type === 'track' || this.exam.type === 'course')) {
        this.warning();
      }

      if (+remaining.hours === 0 && +remaining.minutes <= 2 && this.exam.type === 'practice') {
        this.warning();
      }

      return remaining;
    },
  },
  created() {
    if (process.client) {
      if (!this.exam.completed) {
        this.$store.dispatch('exams/startExamTimer');
      }

      if (!this.exam.completed) {
        this.$once('finished', async () => {
          await this.$store.commit('exams/cleanTimer');
          await this.$store.dispatch('exams/submit', { redirect: false });
          const results = await this.$store.dispatch('modals/open', {
            name: 'default',
            title: 'Your time is up!',
            message: 'You will receive a score and this will count as 1 attempt. You can review your answers in the results section.',
            buttonText: 'See results',
            closable: false,
            className: 'time-up',
          });

          if (results) {
            this.$store.dispatch('exams/redirectToResults');
          }
        });
        this.$once('hook:beforeDestroy', () => this.$store.commit('exams/cleanTimer'));
      }
    }
  },
  methods: {
    warning() {
      this.timewarning = true;
    },
  },
};
</script>

<style lang="scss" scoped>
nav {
  background: var(--main-dark);
  color: var(--true-white);
  display: flex;
  padding: var(--spacing-12) 50px;
  justify-content: space-between;
  height: 64px;
  align-items: center;
  position: relative;

  &.completed {
    padding-left: 80px;
  }

  .back {
    width: 64px;
    position: absolute;
    left: 0;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(249, 250, 251, 0.2);
    cursor: pointer;

    svg {
      width: 10px;
      height: 18px;
    }
  }

  .name {
    font-size: 21px;
    color: var(--main-dark-light-4);
    line-height: 100%;

    b {
      display: block;
      color: var(--main-light);
    }
  }

  &.review-questions .review {
    right: 120px;
    transition-delay: 0s;
  }

  .review {
    font-size: 21px;
    text-align: right;
    font-weight: 500;
    position: relative;
    z-index: 2;
    transition: all 0.1s linear;
    transition-delay: 0.2s;

    .item-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      cursor: pointer;
      font-weight: 500;
      font-size: 21px;
      color: var(--accent);

      svg {
        width: 10px;
        margin-right: var(--spacing-24);
        transition: all 0.3s ease;

        path {
          fill: var(--accent);
        }
      }
    }
  }

  .timer {
    border: 1px solid var(--accent);
    padding: 6px 16px;
    border-radius: 92px;
    display: flex;
    align-items: center;
    min-width: 132px;

    .clock {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      margin-right: var(--spacing-xs);

      svg {
        path {
          fill: var(--accent);
        }
      }
    }

    &.completed {
      opacity: 0.3;
      pointer-events: none;
    }

    &.timewarning {
      border: 1px solid var(--main-red);

      .clock {
        position: relative;

        &:before {
          content: '';
          width: 20px;
          height: 20px;
          background: rgba(255, 80, 80, 0.5);
          border-radius: 50%;
          position: absolute;
        }

        svg {
          transform: scale(1);
          animation: pulse-dot 2s infinite;
          border-radius: 50%;

          path {
            fill: var(--main-red);
          }
        }
      }
    }

    @keyframes pulse-dot {
      0% {
        transform: scale(0.9);
        box-shadow: 0 0 0 6px rgba(255, 82, 82, 0.6);
      }

      70% {
        transform: scale(1);
        box-shadow: 0 0 0 20px rgba(255, 160, 160, 0);
      }

      100% {
        transform: scale(0.9);
        box-shadow: 0 0 0 20px rgba(255, 255, 255, 0);
      }
    }
  }

  @include bp($bp-laptop) {
    padding: var(--spacing-12) var(--spacing-44);

    .name,
    .review .item-wrapper {
      font-size: 18px;
    }

    &.review-questions .review {
      .item-wrapper {
        svg {
          margin-right: var(--spacing-12);
        }
      }
    }
  }
}

</style>
